import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { usePlaylistStore } from '@/store/playlist/store';
import { Button } from '@/components/ui/button';
import { VideoSelectModal, VideoItem } from '@/components/playlist/VideoSelectModal';
import { SortableVideoItem } from '@/components/playlist/SortableVideoItem';
import { Plus, ArrowLeft, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PlaylistVideo } from '@/store/playlist/types';

// Mock de vídeos disponíveis
const mockVideos: VideoItem[] = [
  {
    id: 'vid1',
    name: 'Introdução ao Marketing Digital',
    thumbnail: '/placeholder.svg',
    duration: 320,
    views: 1240,
  },
  {
    id: 'vid2',
    name: 'SEO: Otimização para Motores de Busca',
    thumbnail: '/placeholder.svg',
    duration: 480,
    views: 980,
  },
  {
    id: 'vid3',
    name: 'Google Ads: Primeiros Passos',
    thumbnail: '/placeholder.svg',
    duration: 420,
    views: 1560,
  },
  {
    id: 'vid4',
    name: 'Copywriting para Conversão',
    thumbnail: '/placeholder.svg',
    duration: 360,
    views: 2100,
  },
  {
    id: 'vid5',
    name: 'Email Marketing Avançado',
    thumbnail: '/placeholder.svg',
    duration: 540,
    views: 870,
  },
];

export default function PlaylistAddVideos() {
  useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getPlaylistById, updatePlaylist } = usePlaylistStore();

  const [playlist, setPlaylist] = useState(() => getPlaylistById(id || ''));
  const [videos, setVideos] = useState<PlaylistVideo[]>(playlist?.videos || []);
  const [selectModalOpen, setSelectModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!playlist) {
      toast({
        title: translateInterfaceText("Playlist não encontrada"),
        description: translateInterfaceText("Redirecionando para a lista de playlists..."),
        variant: 'destructive',
      });
      navigate('/playlists');
    }
  }, [playlist, navigate, toast]);

  if (!playlist) return null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setVideos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const reordered = arrayMove(items, oldIndex, newIndex);
        // Update order property
        return reordered.map((item, index) => ({ ...item, order: index }));
      });
    }
  };

  const handleAddVideos = (videoIds: string[]) => {
    const newVideos = videoIds
      .filter((videoId) => !videos.find((v) => v.videoId === videoId))
      .map((videoId, index) => ({
        id: `pv-${Date.now()}-${index}`,
        videoId,
        order: videos.length + index,
      }));

    setVideos([...videos, ...newVideos]);
    toast({
      title: translateInterfaceText("Vídeos adicionados!"),
      description: translateInterfaceText("{value0} vídeo(s) adicionado(s) à playlist.", { value0: (newVideos.length) }),
    });
  };

  const handleRemoveVideo = (videoId: string) => {
    const updated = videos
      .filter((v) => v.id !== videoId)
      .map((v, index) => ({ ...v, order: index }));
    setVideos(updated);
    toast({
      title: translateInterfaceText("Vídeo removido"),
      description: translateInterfaceText("O vídeo foi removido da playlist."),
    });
  };

  const handleSave = () => {
    if (videos.length === 0) {
      toast({
        title: translateInterfaceText("Adicione vídeos"),
        description: translateInterfaceText("A playlist precisa ter pelo menos 1 vídeo."),
        variant: 'destructive',
      });
      return;
    }

    // Calculate total duration
    const totalDuration = videos.reduce((sum, video) => {
      const videoData = mockVideos.find((v) => v.id === video.videoId);
      return sum + (videoData?.duration || 0);
    }, 0);

    updatePlaylist(playlist.id, {
      videos,
      totalDuration,
    });

    toast({
      title: translateInterfaceText("Playlist salva!"),
      description: translateInterfaceText("Os vídeos foram salvos com sucesso."),
    });

    navigate('/playlists');
  };

  const getVideoData = (videoId: string) => {
    const videoData = mockVideos.find((v) => v.id === videoId);
    return (
      videoData || {
        name: 'Vídeo não encontrado',
        thumbnail: '/placeholder.svg',
        duration: 0,
      }
    );
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <button 
                onClick={() => navigate('/playlists')}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {translateInterfaceText("Voltar para Playlists")}</button>
              <h1 className="text-2xl font-bold text-foreground">{playlist.name}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                {translateInterfaceText("Adicione e organize os vídeos da sua playlist")}</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setSelectModalOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {translateInterfaceText("Adicionar Vídeos")}</Button>
              <Button onClick={handleSave} className="bg-[#7A5CFF] hover:bg-[#6B4EE6]">
                <Save className="h-4 w-4 mr-2" />
                {translateInterfaceText("Salvar Playlist")}</Button>
            </div>
          </div>

          {/* Videos List ou Empty State */}
          {videos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-border rounded-xl bg-muted/30">
              <div className="w-16 h-16 rounded-full bg-[#7A5CFF]/10 flex items-center justify-center mb-4">
                <Plus className="h-8 w-8 text-[#7A5CFF]" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {translateInterfaceText("Nenhum vídeo adicionado")}</h3>
              <p className="text-muted-foreground text-sm text-center max-w-md mb-4">
                {translateInterfaceText("Adicione vídeos da sua biblioteca para criar a sequência da playlist.")}</p>
              <Button
                onClick={() => setSelectModalOpen(true)}
                className="bg-[#7A5CFF] hover:bg-[#6B4EE6]"
              >
                <Plus className="h-4 w-4 mr-2" />
                {translateInterfaceText("Adicionar Vídeos")}</Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={videos.map((v) => v.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {videos.map((video, index) => {
                    const videoData = getVideoData(video.videoId);
                    return (
                      <SortableVideoItem
                        key={video.id}
                        video={video}
                        videoData={videoData}
                        index={index}
                        onRemove={handleRemoveVideo}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}

        {/* Video Select Modal */}
        <VideoSelectModal
          isOpen={selectModalOpen}
          onClose={() => setSelectModalOpen(false)}
          availableVideos={mockVideos}
          selectedVideoIds={videos.map((v) => v.videoId)}
          onConfirm={handleAddVideos}
        />
        </div>
      </div>
    </Layout>
  );
}
